import { EWorldMapSetType } from '__generated__/globalTypes';
import { getInitialViewState } from 'components/map/location/defaultMapLocation';
import { useIsWide } from 'libs/utils/logic/ui/responsiveLogic';
import { useAnalytics } from 'libs/utils/site/analytics';
import { CustomActionButton } from 'pages/custom/shared/CustomHeader/CustomActionButton';
import { useContext } from 'react';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    noDetails?: boolean;
    className?: string;
    listTitle?: 'companies' | 'places' | 'list';
    exludeHighlight?: boolean;
}

export const CompanyButtons = (props: Props): JSX.Element => {
    const isWide = useIsWide();
    const ctxView = useContext(CtxView);
    const ctxModal = useContext(CtxModal);
    const analytics = useAnalytics();

    const actionResetZoom = () => {
        const initialMapPosition = getInitialViewState(ctxView.map, isWide);
        if (initialMapPosition.bounds) {
            ctxView.mapRef.current.fitBounds(initialMapPosition.bounds);
        } else {
            ctxView.mapRef.current.flyTo({
                zoom: initialMapPosition.zoom,
                center: [initialMapPosition.longitude, initialMapPosition.latitude]
            });
        }
    };

    const allCategories = ctxView.map.worldMapSets.filter(
        (e) => e.type === EWorldMapSetType.CATEGORY
    );
    const visibleCategories = allCategories.filter((category) =>
        ctxView.visibleMapSetIds?.includes(category.worldMapSetId)
    );

    const getTitle = () => {
        if (props.listTitle === 'places') return Localization.PLACES;
        if (props.listTitle === 'companies') return Localization.COMPANIES;
        return Localization.LIST;
    };

    let titleText = getTitle();
    if (ctxModal.storyList?.visibleStoryIds?.length && ctxView.conectedDetails?.length) {
        const totalCompaniesCount =
            ctxView.conectedDetails?.length -
            (ctxView.customPageConfig.storyIdsExcludedInList?.length ?? 0);

        if (ctxModal.storyList?.visibleStoryIds?.length !== totalCompaniesCount) {
            titleText += ` ${ctxModal.storyList?.visibleStoryIds?.length}/${totalCompaniesCount}`;
        }
    }

    let filtersText = Localization.FILTERS;
    if (visibleCategories?.length && allCategories?.length) {
        filtersText += ` ${visibleCategories?.length}/${allCategories?.length}`;
    }

    return (
        <S_CompanyButtons className={props.className}>
            {!props.noDetails && (
                <S_Button
                    iconSet="far"
                    iconName="building"
                    text={titleText}
                    onClick={() => {
                        analytics.track('custom_button.clicked', {
                            action: 'sidebar_list.opened'
                        });
                        ctxModal.setShowListOfStories(true);
                    }}
                />
            )}

            {Boolean(allCategories.length) && (
                <S_Button
                    iconSet="far"
                    iconName="filter"
                    text={filtersText}
                    onClick={() => {
                        analytics.track('custom_button.clicked', {
                            action: 'sidebar_filters.opened'
                        });
                        ctxModal.setShowFilters(true);
                    }}
                />
            )}

            <S_Button
                iconSet="far"
                iconName="frame"
                text={Localization.ZOOM}
                onClick={(e) => {
                    analytics.track('custom_button.clicked', {
                        action: 'reset_zoom'
                    });
                    actionResetZoom();
                    e.stopPropagation();
                }}
            />

            {!props.noDetails && !props.exludeHighlight && (
                <S_Button
                    iconSet="far"
                    iconName="highlighter"
                    text={
                        ctxView.forceMarkersHighlight
                            ? Localization.UNHIGHLIGHT
                            : Localization.HIGHLIGHT
                    }
                    onClick={(e: React.MouseEvent) => {
                        analytics.track('custom_button.clicked', {
                            action: 'highlight_markers'
                        });
                        ctxView.setForceMarkersHighlight(!ctxView.forceMarkersHighlight);
                        e.stopPropagation();
                    }}
                />
            )}
        </S_CompanyButtons>
    );
};

const S_CompanyButtons = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    @media only screen and (min-width: 1259px) {
        width: unset;
    }
`;

const S_Button = styled(CustomActionButton)`
    width: 100%;
    @media only screen and (min-width: 1259px) {
        min-width: 150px;
        width: unset;
    }
`;
